import { clamp, createLayerCanvas, toDataUrl } from './canvasUtils'

const blurAlpha = (alpha, width, height, radius = 1) => {
  const r = Math.max(0, Math.min(2, Math.round(radius)))
  if (r <= 0) return alpha
  const out = new Uint8ClampedArray(alpha.length)
  const maxX = width - 1
  const maxY = height - 1
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x
      const center = alpha[idx] || 0
      if (center <= 0) continue
      let sum = 0
      let count = 0
      for (let dy = -r; dy <= r; dy += 1) {
        const yy = clamp(y + dy, 0, maxY)
        const row = yy * width
        for (let dx = -r; dx <= r; dx += 1) {
          const xx = clamp(x + dx, 0, maxX)
          sum += alpha[row + xx] || 0
          count += 1
        }
      }
      out[idx] = Math.round(sum / Math.max(1, count))
    }
  }
  return out
}

const dilateAlpha = (alpha, width, height, strength = 0.8) => {
  const out = new Uint8ClampedArray(alpha)
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const idx = y * width + x
      if ((alpha[idx] || 0) > 0) continue
      let best = 0
      for (let dy = -1; dy <= 1; dy += 1) {
        const row = (y + dy) * width
        for (let dx = -1; dx <= 1; dx += 1) {
          best = Math.max(best, alpha[row + x + dx] || 0)
        }
      }
      if (best > 0) out[idx] = Math.round(best * Math.max(0, Math.min(1, strength)))
    }
  }
  return out
}

const sampleBilinear = (data, width, height, fx, fy) => {
  const x0 = clamp(Math.floor(fx), 0, width - 1)
  const x1 = clamp(x0 + 1, 0, width - 1)
  const y0 = clamp(Math.floor(fy), 0, height - 1)
  const y1 = clamp(y0 + 1, 0, height - 1)
  const tx = fx - x0
  const ty = fy - y0
  const row0 = y0 * width
  const row1 = y1 * width
  const i00 = data[row0 + x0] || 0
  const i10 = data[row0 + x1] || 0
  const i01 = data[row1 + x0] || 0
  const i11 = data[row1 + x1] || 0
  const i0 = i00 + (i10 - i00) * tx
  const i1 = i01 + (i11 - i01) * tx
  return i0 + (i1 - i0) * ty
}

export const buildHairLayer = ({
  hairSegmenterImage,
  image,
  width,
  height,
  hairEnhance = false,
  hairEdgeQuality = 'smooth',
  hairMaskSampling = 'bilinear'
}) => {
  if (!hairSegmenterImage || !image) return null
  const { canvas, ctx } = createLayerCanvas(width, height)
  const imageData = ctx.createImageData(width, height)
  const alphaOut = new Uint8ClampedArray(width * height)
  let hasHair = false
  const useNearest = hairMaskSampling === 'nearest'
  const confMin = hairEnhance ? 0.26 : 0.33
  const confMax = hairEnhance ? 0.8 : 0.85
  const baseAlpha =
    hairEdgeQuality === 'off' ? 130 : hairEdgeQuality === 'supersample' ? 105 : 110
  const alphaScale =
    hairEdgeQuality === 'off' ? 120 : hairEdgeQuality === 'supersample' ? 115 : 110
  const alphaMax =
    hairEdgeQuality === 'off' ? 235 : hairEdgeQuality === 'supersample' ? 220 : 210

  const writePixel = (p, alpha) => {
    if (alpha <= 0) return
    alphaOut[p] = Math.max(alphaOut[p] || 0, alpha)
    hasHair = true
  }

  const seg = hairSegmenterImage.segment(image)
  const confidenceMasks = seg?.confidenceMasks
  if (confidenceMasks?.length) {
    const hairMask = confidenceMasks[1] || confidenceMasks[0]
    const floats = hairMask?.getAsFloat32Array?.()
    const maskWidth = hairMask?.width || 0
    const maskHeight = hairMask?.height || 0
    if (floats?.length && maskWidth > 0 && maskHeight > 0) {
      const sx = (maskWidth - 1) / Math.max(1, width - 1)
      const sy = (maskHeight - 1) / Math.max(1, height - 1)
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const fx = x * sx
          const fy = y * sy
          const confidence = useNearest
            ? clamp(floats[clamp(Math.round(fy), 0, maskHeight - 1) * maskWidth + clamp(Math.round(fx), 0, maskWidth - 1)] || 0, 0, 1)
            : clamp(sampleBilinear(floats, maskWidth, maskHeight, fx, fy), 0, 1)
          if (confidence < confMin) continue
          const alpha =
            confidence >= confMax
              ? alphaMax
              : Math.floor(baseAlpha + confidence * alphaScale)
          writePixel(y * width + x, alpha)
        }
      }
    }
  }

  if (!hasHair) {
    const categoryMask = seg?.categoryMask?.getAsImageData?.()
    if (categoryMask?.data?.length) {
      const maskWidth = categoryMask.width || width
      const maskHeight = categoryMask.height || height
      const sx = (maskWidth - 1) / Math.max(1, width - 1)
      const sy = (maskHeight - 1) / Math.max(1, height - 1)
      const channel = new Float32Array(maskWidth * maskHeight)
      for (let p = 0; p < channel.length; p += 1) {
        channel[p] = ((categoryMask.data[p * 4] || 0) / 255) * (hairEnhance ? 1 : 0.9)
      }
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const fx = x * sx
          const fy = y * sy
          const conf = useNearest
            ? channel[
                clamp(Math.round(fy), 0, maskHeight - 1) * maskWidth +
                  clamp(Math.round(fx), 0, maskWidth - 1)
              ] || 0
            : sampleBilinear(channel, maskWidth, maskHeight, fx, fy)
          if (conf <= 0.02) continue
          writePixel(y * width + x, Math.round(conf * 190))
        }
      }
    }
  }

  if (!hasHair) return null
  let finalAlpha = alphaOut
  if (hairEnhance) {
    finalAlpha = dilateAlpha(finalAlpha, width, height, 0.84)
  }
  if (hairEdgeQuality === 'smooth') {
    finalAlpha = blurAlpha(finalAlpha, width, height, 1)
  } else if (hairEdgeQuality === 'supersample') {
    finalAlpha = blurAlpha(finalAlpha, width, height, 2)
  }
  for (let p = 0; p < width * height; p += 1) {
    const a = finalAlpha[p] || 0
    if (a <= 0) continue
    const di = p * 4
    imageData.data[di] = 16
    imageData.data[di + 1] = 185
    imageData.data[di + 2] = 129
    imageData.data[di + 3] = a
  }
  ctx.putImageData(imageData, 0, 0)
  return toDataUrl(canvas)
}
